<template>
	<Dialog
		:dialog-visible="dialogVisible"
		title="Share Report"
		:remove-footer="true"
		:options="{ width: '520px' }"
		@cancel="emit('cancel')">
		<template #dialog-header>
			<v-btn
				icon="icon-close-medium"
				variant="text"
				density="comfortable"
				class="close-btn"
				aria-label="Close"
				@click="emit('cancel')" />
		</template>

		<template #dialog-body>
			<div class="d-flex flex-column ga-4">
				<!-- Copy link -->
				<div>
					<div class="text-body-2 font-weight-bold mb-2">Report Link</div>
					<div v-if="linkError" class="text-body-2 text-error">
						We couldn't generate a share link. Please close this and try again.
					</div>
					<div v-else class="d-flex ga-2">
						<v-text-field
							data-test-id="report-share-link"
							:model-value="displayUrl"
							readonly
							density="compact"
							hide-details
							:loading="preparingLink"
							@focus="($event.target as HTMLInputElement)?.select()" />
						<PrimaryBtn
							:min-width="100"
							:disabled="preparingLink || !displayUrl"
							@click="copyLink">
							{{ copied ? "Copied!" : "Copy" }}
						</PrimaryBtn>
					</div>
				</div>

				<v-divider />

				<!-- Share via text (SMS) -->
				<div>
					<div class="d-flex align-center ga-2 mb-2">
						<v-icon size="18" color="primary">mdi-message-text-outline</v-icon>
						<div class="text-body-2 font-weight-bold">Share via Text</div>
					</div>
					<RecipientPicker
						ref="smsPickerRef"
						v-model="smsRecipients"
						channel="phone"
						test-id="report-share-sms-recipient-picker"
						label="Contacts or phone numbers"
						placeholder="Pick a contact or type a phone number"
						helper-text="Type to search contacts or enter a phone number."
						:customers="allCustomers"
						:loading="loadingCustomers"
						:max-recipients="MAX_RECIPIENTS_PER_CHANNEL"
						:default-country="currentOrganizationDefaultCountry" />
				</div>

				<!-- Share via email -->
				<div>
					<div class="d-flex align-center ga-2 mb-2">
						<v-icon size="18" color="primary">mdi-email-outline</v-icon>
						<div class="text-body-2 font-weight-bold">Share via Email</div>
					</div>
					<RecipientPicker
						ref="emailPickerRef"
						v-model="emailRecipients"
						channel="email"
						label="Contacts or email addresses"
						placeholder="Pick a contact or type an email"
						helper-text="Type to search contacts or enter an email address."
						:customers="allCustomers"
						:loading="loadingCustomers"
						:max-recipients="MAX_RECIPIENTS_PER_CHANNEL" />
				</div>

				<v-divider />

				<PrimaryBtn
					:loading="sending"
					@click="sendShare">
					Send
				</PrimaryBtn>
			</div>
		</template>
	</Dialog>
</template>

<script lang="ts" setup>
	import { ref, computed, watch, nextTick } from "vue";
	import { useToast } from "vue-toastification";
	import Dialog from "@/components/Dialog.vue";
	import RecipientPicker from "@/components/RecipientPicker.vue";
	import { copyToClipboard, calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import { logError, logInfo } from "@/helpers/logError";
	import type { ContactRecipient } from "@/composables/useContactRecipientSearch";
	import { useOrganization } from "@/composables/useOrganization";
	import { summarizeSendOutcome, partitionFailedRecipients, hasInvalidRecipients, type SendResult } from "@/composables/useReportShare";
	import Dialogs from "@/helpers/dialogs";
	import api from "@/api";
	import { publishAnalysis } from "@/api/scribe";

	interface Props {
		dialogVisible: boolean;
		analysisId: string;
		externalType: string;
		externalId: string;
		reportTitle?: string;
	}

	const props = defineProps<Props>();
	const emit = defineEmits<{
		(event: "cancel"): void;
	}>();

	const toast = useToast();

	const MAX_RECIPIENTS_PER_CHANNEL = 10;

	const copied = ref(false);
	const sending = ref(false);
	const preparingLink = ref(false);
	const linkError = ref(false);
	const allCustomers = ref<unknown[]>([]);
	const loadingCustomers = ref(false);
	const emailRecipients = ref<ContactRecipient[]>([]);
	const smsRecipients = ref<ContactRecipient[]>([]);
	const publicUrl = ref<string | null>(null);
	const shortUrl = ref<string | null>(null);

	// Refs to the two pickers so Send can flush any typed-but-not-Entered input before reading
	// recipients (LSP-16630). Only the flush method is needed from each picker's exposed surface.
	type RecipientPickerExposed = { flushPendingInput: () => void };
	const smsPickerRef = ref<RecipientPickerExposed | null>(null);
	const emailPickerRef = ref<RecipientPickerExposed | null>(null);

	const { currentOrganizationDefaultCountry } = useOrganization();

	// Prefer the short URL when one has been generated; fall back to the publish URL so users
	// always see something copyable if the shortener is slow or fails.
	const displayUrl = computed(() => shortUrl.value ?? publicUrl.value ?? "");

	// Tag the scribe link with `resolve_auth=false` so the public report page skips resolving the
	// private report — that suppresses its "you have access to the full report" banner, which
	// points back at the Scribe app (context our Contact users don't have). Query string only, so
	// the link's origin is unchanged and the API's anti-relay check still passes.
	function withResolveAuthDisabled(rawUrl: string): string {
		try {
			const url = new URL(rawUrl);
			url.searchParams.set("resolve_auth", "false");
			return url.toString();
		} catch (err) {
			// Defensive: if scribe ever returns a non-absolute URL, share it unmodified — but
			// log it, since the untagged link silently defeats the banner suppression. Mirrors
			// the logError on the sibling publish/shorten/contacts paths in this watcher.
			logError("ShareReportDialog: failed to tag report URL with resolve_auth=false", err, {
				analysisId: props.analysisId,
				rawUrl
			});
			return rawUrl;
		}
	}

	// Any invalid (red) pill in either channel blocks send (LSP-16629 / LSP-16630) — the per-picker
	// red chips and inline errors tell the user which entries to fix. Scan each list directly (no
	// combined-array allocation); the email scan short-circuits before touching SMS.
	const anyRecipientInvalid = computed(
		() => hasInvalidRecipients(emailRecipients.value) || hasInvalidRecipients(smsRecipients.value)
	);
	const hasAnyRecipient = computed(() => emailRecipients.value.length > 0 || smsRecipients.value.length > 0);

	// Monotonic token to ignore stale async completions. If the dialog is reopened — or pointed at
	// a different report — while a publish/shorten/contacts load is still in flight, the older run's
	// late assignments are dropped, so we never display or send another report's scribe link.
	let linkLoadToken = 0;

	// Load contacts and publish+shorten the report link in parallel when the dialog opens — and
	// re-run if analysisId changes while the dialog stays open (Share switched to another report),
	// so the link never lags behind the current props. Publishing is idempotent — it preserves an
	// existing token on re-publish, so re-opening the dialog is safe.
	watch(
		() => [props.dialogVisible, props.analysisId] as const,
		async ([visible]) => {
			if (!visible) return;
			const requestToken = ++linkLoadToken;
			emailRecipients.value = [];
			smsRecipients.value = [];
			allCustomers.value = [];
			publicUrl.value = null;
			shortUrl.value = null;
			linkError.value = false;
			copied.value = false;
			loadingCustomers.value = true;
			preparingLink.value = true;

			// Drop a result if a newer open has superseded this run (see linkLoadToken).
			const isCurrent = () => requestToken === linkLoadToken;

			const contactsTask = (async () => {
				try {
					// 9999: orgs with >1k customers would otherwise see silent truncation in the pickers.
					const customers = (await Dialogs.getCustomers("", 9999)) ?? [];
					if (isCurrent()) allCustomers.value = customers;
				} catch (err) {
					logError("ShareReportDialog: failed to load contacts", err, { analysisId: props.analysisId });
					if (isCurrent()) allCustomers.value = [];
				} finally {
					if (isCurrent()) loadingCustomers.value = false;
				}
			})();

			const linkTask = (async () => {
				try {
					const published = await publishAnalysis(props.analysisId);
					if (!isCurrent()) return;
					const taggedUrl = withResolveAuthDisabled(published.publicUrl);
					publicUrl.value = taggedUrl;
					try {
						const result = await api.createShortenedUrl(taggedUrl);
						if (isCurrent() && result?.url) shortUrl.value = result.url;
					} catch (err) {
						// Non-fatal — the dialog still shows the long public URL.
						logError("ShareReportDialog: failed to shorten report URL", err, { analysisId: props.analysisId });
					}
				} catch (err) {
					if (isCurrent()) linkError.value = true;
					logError("ShareReportDialog: failed to publish report", err, { analysisId: props.analysisId });
				} finally {
					if (isCurrent()) preparingLink.value = false;
				}
			})();

			await Promise.allSettled([contactsTask, linkTask]);
		},
		{ immediate: true }
	);

	async function copyLink() {
		const success = await copyToClipboard(displayUrl.value);
		if (success) {
			copied.value = true;
			const message = "Report link copied to clipboard!";
			toast.success(message, { timeout: calculateTimeout(message) });
			setTimeout(() => {
				copied.value = false;
			}, 2000);
		} else {
			const message = "Failed to copy link to clipboard.";
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}

	// LSP-20004: Send is never greyed out — it used to bind `:disabled` to the recipient validity, the
	// link error and the link-preparation flag, so a customer with an empty or invalid form got a dead
	// button and no explanation. Per CLAUDE.md "Form action buttons — always enabled, no-op when
	// invalid" the button stays clickable and EVERY reason a send can't proceed is answered here, with
	// a toast, and without submitting. `:loading="sending"` still covers the in-flight submit, which is
	// the sanctioned exception (double-submit protection), and the guard below makes that explicit.
	async function sendShare() {
		if (sending.value) return;

		// Commit any typed-but-not-Entered values first so a pending recipient isn't dropped on Send
		// (LSP-16630). flushPendingInput is a no-op when the field is empty.
		smsPickerRef.value?.flushPendingInput();
		emailPickerRef.value?.flushPendingInput();
		await nextTick();

		// A just-flushed value may be invalid — block the send and let the per-picker red pills and
		// inline errors show the user exactly what to fix (LSP-16629 / LSP-16630). The invalid entries
		// are deliberately left in place so the customer can correct them.
		if (anyRecipientInvalid.value) {
			const message = "Some recipients aren't valid yet — fix the highlighted ones and try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
			return;
		}
		if (!hasAnyRecipient.value) {
			const message = "Add a recipient — a contact, phone number or email address — to share this report.";
			toast.info(message, { timeout: calculateTimeout(message) });
			return;
		}
		if (preparingLink.value) {
			const message = "Still preparing the report link — try Send again in a moment.";
			toast.info(message, { timeout: calculateTimeout(message) });
			return;
		}
		// linkError is the publish failure; a missing publicUrl with no error flag is the same dead end
		// from the customer's side, and used to be a silent early return.
		if (linkError.value || !publicUrl.value) {
			const message = "We couldn't generate a share link. Please close this and try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
			logInfo("ShareReportDialog: Send blocked with no usable report link", {
				analysisId: props.analysisId,
				linkError: linkError.value,
				hasPublicUrl: !!publicUrl.value
			});
			return;
		}
		sending.value = true;
		try {
			const result = (await api.notifyReportShare({
				publicUrl: publicUrl.value,
				reportName: props.reportTitle,
				externalType: props.externalType,
				externalId: props.externalId,
				emails: emailRecipients.value.map((recipient) => recipient.value),
				phoneNumbers: smsRecipients.value.map((recipient) => recipient.value)
			})) as SendResult;
			renderSendOutcome(result);
		} catch (err) {
			logError("ShareReportDialog: share send failed", err, {
				analysisId: props.analysisId,
				emailCount: emailRecipients.value.length,
				phoneCount: smsRecipients.value.length
			});
			const message = getMessageFromError(err) || "Something went wrong sending the report. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		} finally {
			sending.value = false;
		}
	}

	function renderSendOutcome(result: SendResult) {
		const outcome = summarizeSendOutcome(result);
		toast[outcome.kind](outcome.message, { timeout: calculateTimeout(outcome.message) });
		if (outcome.kind === "success") {
			emailRecipients.value = [];
			smsRecipients.value = [];
			emit("cancel");
		} else if (outcome.kind === "warning") {
			emailRecipients.value = partitionFailedRecipients(emailRecipients.value, result.failedEmails);
			smsRecipients.value = partitionFailedRecipients(smsRecipients.value, result.failedPhoneNumbers);
		}
	}
</script>

<style scoped>
	.close-btn {
		position: absolute;
		top: 8px;
		right: 8px;
	}
</style>
